/* eslint-disable max-len */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CifaAPI from 'api/cifa/cifa.api';
import AlertModal from 'components/shared/alert_modal';
import DataConfirmation from 'components/shared/data_confirmation';
import { PageTitle } from 'components/shared/page_title';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import useCifaStore from 'stores/cifa/cifa.store';
import {
  getCPFFormatado,
  getCNPJFormatado,
  validarCPF,
  validarCNPJ,
  getMascaraMoeda,
  getFloat,
} from 'utils/uteis';
import { formatCurrency } from 'utils/format_currency';
import {
  getSecaoKey,
  formatarValorInput,
  computeTotais,
  buildPayload,
  applyEdicao,
  confirmarExclusaoHelper,
  formatarValorParaExibicao,
  validateProdutoEdit,
  getDocumentHelperTextFn,
} from '../shared/step2Helpers';
export {
  getSecaoKey,
  formatarValorInput,
  computeTotais,
  buildPayload,
  applyEdicao,
  confirmarExclusaoHelper,
  formatarValorParaExibicao,
  validateProdutoEdit,
} from '../shared/step2Helpers';
export {
  convertToApiFormat,
  calcularTotais,
  editarProdutoInSecoes,
  excluirProdutoFromSecoes,
  validarProdutoParaSalvar,
  getDocumentHelperTextFn,
} from '../shared/step2Helpers';

interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}



// Constrói o objeto de campos a atualizar a partir dos valores do formik
export const buildFieldsToUpdate = (values: any) => ({
  alimentacao_af_recurso_pnae: Number(values.alimentacao_af_recurso_pnae) || 0,
  alimentacao_af_recurso_estado: Number(values.alimentacao_af_recurso_estado) || 0,
  alimentacao_af_recurso_prefeitura: Number(values.alimentacao_af_recurso_prefeitura) || 0,
  alimentacao_total_recurso_pnae: Number(values.alimentacao_total_recurso_pnae) || 0,
  alimentacao_total_recurso_estado: Number(values.alimentacao_total_recurso_estado) || 0,
  alimentacao_total_recurso_prefeitura: Number(values.alimentacao_total_recurso_prefeitura) || 0,
  alimentacao_ata_aprovacao_uuid: values.alimentacao_ata_aprovacao_uuid,
});

interface Step2Props {
  onBack: () => void;
  formik: any;
}

const Step2: React.FC<Step2Props> = ({ onBack, formik }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localSecoes, setLocalSecoes] = useState<SecaoAquisicao[]>(
    formik.values.secoesAquisicao || []
  );
  const [produtoEditando, setProdutoEditando] = useState<Produto | null>(null);
  const [secaoEditando, setSecaoEditando] = useState<number | null>(null);
  const [tipoEditando, setTipoEditando] = useState<'cnpj' | 'cpf' | null>(null);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [produtoExcluir, setProdutoExcluir] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { cifa, setCifa } = useCifaStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);

  const anoReferencia = match ? Number(match[1]) : 2026;

  // Sincronizar localSecoes com formik.values sempre que mudar
  useEffect(() => {
    if (formik.values.secoesAquisicao && Array.isArray(formik.values.secoesAquisicao)) {
      setLocalSecoes(formik.values.secoesAquisicao);
    }
  }, [formik.values.secoesAquisicao]);
  const { totaisPorSecao, totalGeral } = useMemo(() => computeTotais(localSecoes), [localSecoes]);

  const handleEditar = (
    secaoIndex: number,
    tipo: 'cnpj' | 'cpf',
    produto: Produto,
  ) => {
    setSecaoEditando(secaoIndex);
    setTipoEditando(tipo);
    setProdutoEditando({ ...produto });
  };

  const handleDocumentoChange = (value: string, tipo: 'cnpj' | 'cpf') => {
    if (!produtoEditando) return;

    const formattedValue =
      tipo === 'cnpj'
        ? getCNPJFormatado(value.replace(/\D/g, ''))
        : getCPFFormatado(value.replace(/\D/g, ''));

    setProdutoEditando({
      ...produtoEditando,
      documento: formattedValue,
    });
  };

  const getDocumentHelperText = () => getDocumentHelperTextFn(tipoEditando, produtoEditando);

  const handleSalvarEdicao = () => {
    if (!produtoEditando || secaoEditando === null || !tipoEditando) return;

    const validation = validateProdutoEdit(produtoEditando, tipoEditando);
    if (!validation.ok) {
      if (validation.error) {
        setSnackbar({ open: true, message: validation.error, severity: 'error' });
      }
      return;
    }

    const valorNumerico = validation.valorNumerico as number;

    const updatedSecoes = [...localSecoes];
    const produtoAtualizado = {
      ...produtoEditando,
      valorNumerico,
      valor: getMascaraMoeda(valorNumerico),
    };

    if (tipoEditando === 'cnpj') {
      updatedSecoes[secaoEditando].produtosCnpj = updatedSecoes[
        secaoEditando
      ].produtosCnpj.map((p) =>
        p.id === produtoEditando.id ? produtoAtualizado : p,
      );
    } else {
      updatedSecoes[secaoEditando].produtosCpf = updatedSecoes[
        secaoEditando
      ].produtosCpf.map((p) =>
        p.id === produtoEditando.id ? produtoAtualizado : p,
      );
    }

    setLocalSecoes(updatedSecoes);
    // Atualiza o formik também
    formik.setFieldValue('secoesAquisicao', updatedSecoes);
    setProdutoEditando(null);
    setSnackbar({
      open: true,
      message: 'Alterações salvas com sucesso',
      severity: 'success',
    });
  };



  const handleExcluir = (id: string) => {
    setProdutoExcluir(id);
    setOpenConfirmacao(true);
  };

  const confirmarExclusao = () => {
    if (!produtoExcluir) return;
    const updatedSecoes = confirmarExclusaoHelper(localSecoes, produtoExcluir);

    setLocalSecoes(updatedSecoes);
    // Atualiza o formik também
    formik.setFieldValue('secoesAquisicao', updatedSecoes);
    setOpenConfirmacao(false);
    setSnackbar({
      open: true,
      message: 'Item excluído com sucesso',
      severity: 'success',
    });
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Converter as seções de aquisição para o formato da API (usa convertToApiFormat exportado)
      // Garantir que os valores são números
      const fieldsToUpdate = buildFieldsToUpdate(formik.values);

      // Dados das aquisições (separado)
      const aquisicoes = buildPayload(localSecoes);

      // Primeira chamada: salvar valores de alimentação
      await CifaAPI.updateSchoolFeeding(cifa.uuid as string, fieldsToUpdate);

      // Segunda chamada: salvar aquisições (usar updateSchoolFeedingAcquisitions)
      await CifaAPI.updateSchoolFeedingAcquisitions(cifa.uuid as string, aquisicoes);

      // Atualizar o store com ambos os dados
      const updatedCifa = {
        ...cifa,
        ...fieldsToUpdate,
        ...aquisicoes
      };

      setCifa(updatedCifa);

      setIsModalOpen(true);
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao salvar os dados',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string);
  };

  return (
    <>
      <PageTitle
        title='Confirmação dos Dados de Alimentação Escolar'
        subtitle='Confira todas as informações sobre os valores de alimentação escolar que foram preenchidas, e altere-as caso necessário.'
      />

      <Stack marginX='4rem' spacing='2rem' maxWidth='1152px'>
        <DataConfirmation
          title='Valores Aplicados nas Aquisições da Agricultura Familiar'
          data={[
            {
              label: 'Recurso do PNAE Federal',
              value: formatarValorParaExibicao(formik.values.alimentacao_af_recurso_pnae),
            },
            {
              label: 'Recurso do Governo do Estado',
              value: formatarValorParaExibicao(formik.values.alimentacao_af_recurso_estado),
            },
            {
              label: 'Recurso da Prefeitura',
              value: formatarValorParaExibicao(formik.values.alimentacao_af_recurso_prefeitura),
            },
            {
              label: 'Total de Recursos da Agricultura Familiar',
              value: formatarValorParaExibicao(
                Number(formik.values.alimentacao_af_recurso_pnae || 0) +
                Number(formik.values.alimentacao_af_recurso_estado || 0) +
                Number(formik.values.alimentacao_af_recurso_prefeitura || 0)
              ),
            },
          ]}
        />

        <DataConfirmation
          title='Valores Totais Aplicados na Alimentação Escolar'
          data={[
            {
              label: 'Total de Recursos PNAE Federal',
              value: formatarValorParaExibicao(formik.values.alimentacao_total_recurso_pnae),
            },
            {
              label: 'Total de Recursos do Governo do Estado',
              value: formatarValorParaExibicao(formik.values.alimentacao_total_recurso_estado),
            },
            {
              label: 'Total de Recursos da Prefeitura',
              value: formatarValorParaExibicao(formik.values.alimentacao_total_recurso_prefeitura),
            },
            {
              label: 'Total Geral de Recursos na Alimentação Escolar',
              value: formatarValorParaExibicao(
                Number(formik.values.alimentacao_total_recurso_pnae || 0) +
                Number(formik.values.alimentacao_total_recurso_estado || 0) +
                Number(formik.values.alimentacao_total_recurso_prefeitura || 0)
              ),
            },
          ]}
        />

        <DataConfirmation
          title='Documentação de Aprovação'
          data={[
            {
              label: 'Ata de Aprovação dos Dados de Alimentação Escolar',
              value: formik.values.alimentacao_ata_aprovacao_uuid ? 'Anexada' : 'Não anexada',
            },
          ]}
          document_uuid={formik.values.alimentacao_ata_aprovacao_uuid}
          document_show={!!formik.values.alimentacao_ata_aprovacao_uuid}

        />

        {/* Seção de Aquisições Detalhadas */}
        {localSecoes.length > 0 && (
          <>
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>
                Resumo Financeiro das Aquisições
              </Typography>
              <Grid container spacing={2}>
                {localSecoes.map((secao, index) => (
                  <Grid item xs={12} md={4} key={getSecaoKey(secao, index)}>
                    <Typography variant='subtitle1'>{secao.titulo}</Typography>
                    <Typography>
                      CNPJ: {formatCurrency(totaisPorSecao[index]?.totalCnpj || 0)}
                    </Typography>
                    <Typography>
                      CPF: {formatCurrency(totaisPorSecao[index]?.totalCpf || 0)}
                    </Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Total:{' '}
                      {formatCurrency(
                        (totaisPorSecao[index]?.totalCnpj || 0) +
                        (totaisPorSecao[index]?.totalCpf || 0),
                      )}
                    </Typography>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                    Total Geral das Aquisições: {formatCurrency(totalGeral)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {localSecoes.map((secao, secaoIndex) => (
              <Box key={getSecaoKey(secao, secaoIndex)} sx={{ mb: 4 }}>
                <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
                  {secao.titulo}
                </Typography>

                {secao.produtosCnpj.length > 0 && (
                  <>
                    <Typography variant='subtitle1' sx={{ mb: 1 }}>
                      Fornecedores CNPJ
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableRow>
                            <TableCell><strong>Razão Social</strong></TableCell>
                            <TableCell><strong>CNPJ</strong></TableCell>
                            <TableCell><strong>Valor</strong></TableCell>
                            <TableCell align='right'><strong>Ações</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {secao.produtosCnpj.map((produto) => (
                            <TableRow key={produto.id}>
                              <TableCell>{produto.nomeRazao}</TableCell>
                              <TableCell>{produto.documento}</TableCell>
                              <TableCell>{produto.valor}</TableCell>
                              <TableCell align='right'>
                                <IconButton
                                  onClick={() => handleEditar(secaoIndex, 'cnpj', produto)}
                                >
                                  <EditIcon color='primary' />
                                </IconButton>
                                <IconButton onClick={() => handleExcluir(produto.id)}>
                                  <DeleteIcon color='error' />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {secao.produtosCpf.length > 0 && (
                  <>
                    <Typography variant='subtitle1' sx={{ mb: 1 }}>
                      Fornecedores CPF
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableRow>
                            <TableCell><strong>Nome</strong></TableCell>
                            <TableCell><strong>CPF</strong></TableCell>
                            <TableCell><strong>Valor</strong></TableCell>
                            <TableCell align='right'><strong>Ações</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {secao.produtosCpf.map((produto) => (
                            <TableRow key={produto.id}>
                              <TableCell>{produto.nomeRazao}</TableCell>
                              <TableCell>{produto.documento}</TableCell>
                              <TableCell>{produto.valor}</TableCell>
                              <TableCell align='right'>
                                <IconButton
                                  onClick={() => handleEditar(secaoIndex, 'cpf', produto)}
                                >
                                  <EditIcon color='primary' />
                                </IconButton>
                                <IconButton onClick={() => handleExcluir(produto.id)}>
                                  <DeleteIcon color='error' />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            ))}
          </>
        )}

        <Stack
          direction='row'
          spacing={2}
          justifyContent='center'
          alignItems='center'
          marginTop='2rem'
        >
          <Button variant='text' color='primary' onClick={onBack} disabled={isSubmitting}>
            Voltar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSave}
            disabled={isSubmitting}
            sx={{ minWidth: '140px' }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Confirmar e Salvar'
            )}
          </Button>
        </Stack>

        <AlertModal
          open={isModalOpen}
          title='Dados de Alimentação Escolar Salvos com Sucesso'
          message={`Os valores de alimentação escolar foram salvos com sucesso. Continue realizando o preenchimento das próximas etapas para concluir o cadastro do Município no CIFA ${anoReferencia}.`}
          buttonLabel='Avançar'
          onClose={handleCloseModal}
        />

        {/* Dialog de Edição */}
        <Dialog
          open={!!produtoEditando}
          onClose={() => setProdutoEditando(null)}
          fullWidth
          maxWidth='sm'
        >
          <DialogTitle>Editar Fornecedor</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              label='Nome/Razão Social'
              fullWidth
              value={produtoEditando?.nomeRazao ?? ''}
              onChange={(e) => setProdutoEditando({
                ...produtoEditando!,
                nomeRazao: e.target.value.toLocaleUpperCase()
              })}
              sx={{ mb: 3 }}
            />

            <TextField
              label={tipoEditando === 'cnpj' ? 'CNPJ' : 'CPF'}
              fullWidth
              value={produtoEditando?.documento ?? ''}
              onChange={(e) =>
                handleDocumentoChange(e.target.value, tipoEditando!)
              }
              inputProps={{
                maxLength: tipoEditando === 'cnpj' ? 18 : 14,
              }}
              sx={{ mb: 3 }}
              error={
                (tipoEditando === 'cnpj' &&
                  !validarCNPJ(produtoEditando?.documento ?? '')) ||
                (tipoEditando === 'cpf' &&
                  !validarCPF(produtoEditando?.documento ?? ''))
              }
              helperText={getDocumentHelperText()}
            />

            <TextField
              label='Valor'
              fullWidth
              value={produtoEditando?.valor ?? ''}
              onChange={(e) =>
                setProdutoEditando({
                  ...produtoEditando!,
                  valor: formatarValorInput(e.target.value),
                })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProdutoEditando(null)}>Cancelar</Button>
            <Button
              variant='contained'
              onClick={handleSalvarEdicao}
              disabled={
                !produtoEditando?.nomeRazao ||
                (tipoEditando === 'cnpj' &&
                  !validarCNPJ(produtoEditando?.documento || '')) ||
                (tipoEditando === 'cpf' &&
                  !validarCPF(produtoEditando?.documento || '')) ||
                !produtoEditando?.valorNumerico ||
                produtoEditando?.valorNumerico <= 0
              }
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog open={openConfirmacao} onClose={() => setOpenConfirmacao(false)}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <Typography>Tem certeza que deseja excluir este item?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmacao(false)}>Cancelar</Button>
            <Button variant='contained' color='error' onClick={confirmarExclusao}>
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Notificações */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Stack>
    </>
  );
};

export default Step2;
